import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    Home as HomeIcon,
    Group as GroupIcon,
    EmojiPeople as EmojiPeopleIcon,
    MeetingRoom as MeetingRoomIcon,
    Kitchen as KitchenIcon,
    Wifi as WifiIcon,
    WbSunny as WbSunnyIcon,
    LocalLaundryService as LocalLaundryServiceIcon,
    Lock as LockIcon,
    Nature as NatureIcon,
    TipsAndUpdates as TipsAndUpdatesIcon,
    Star as StarIcon,
    CheckCircle as CheckCircleIcon,
    FormatListNumbered as FormatListNumberedIcon,
    MenuBook as MenuBookIcon,
    Comment as CommentIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに', icon: <HomeIcon /> },
    { id: 'points', label: '住み心地を左右する10のポイント', icon: <FormatListNumberedIcon /> },
    { id: 'tips', label: '生活の工夫と住み心地向上アイデア', icon: <TipsAndUpdatesIcon /> },
    { id: 'experiences', label: '体験談から学ぶ住み心地アップのコツ', icon: <CommentIcon /> },
    { id: 'summary', label: 'まとめ', icon: <StarIcon /> }
];

const roomSharePoints = [
    {
        icon: <MeetingRoomIcon color="primary" />,
        title: '1. パーソナルスペースの確保',
        description: '各自のプライベート空間がしっかり確保されているかは、ルームシェア生活の満足度を大きく左右します。部屋割りやパーテーション、収納スペースの工夫が重要です。'
    },
    {
        icon: <GroupIcon color="secondary" />,
        title: '2. 共有スペースの広さと明るさ',
        description: 'リビングやダイニングなどの共用スペースが広めで、自然光がたっぷり入る設計だと、日常の交流もスムーズ。居心地の良さとコミュニケーションの質が向上します。'
    },
    {
        icon: <KitchenIcon color="success" />,
        title: '3. 充実したキッチン設備',
        description: '複数人で使ってもストレスが少ない広さや、十分な収納・調理家電が整っているかも要チェック。冷蔵庫の大きさやシンクの数も見逃せません。'
    },
    {
        icon: <LocalLaundryServiceIcon color="info" />,
        title: '4. 使いやすい水回り（バス・トイレ・洗面）',
        description: '朝の混雑を避けるためにも、バス・トイレが複数ある物件は人気。清潔に保ちやすい設備であることも快適な生活には欠かせません。'
    },
    {
        icon: <WifiIcon color="primary" />,
        title: '5. 高速インターネット環境',
        description: '在宅ワークやオンライン学習が増える中、安定したWi-Fiは必須条件。通信速度やルーターの設置場所も確認しましょう。'
    },
    {
        icon: <WbSunnyIcon style={{ color: '#ffb300' }} />,
        title: '6. 日当たり・通風の良さ',
        description: '部屋が暗いと気分も沈みがち。南向きや窓の多い間取り、風通しの良さは住み心地に直結します。'
    },
    {
        icon: <LockIcon style={{ color: '#8e24aa' }} />,
        title: '7. セキュリティと安心感',
        description: '玄関のオートロックや個室の鍵、女性専用エリアの有無など、防犯面も重視しましょう。安心して眠れる環境が基本です。'
    },
    {
        icon: <NatureIcon color="success" />,
        title: '8. 清潔さと掃除の仕組み',
        description: '掃除当番やルールが明確な物件は、常に快適な環境を保ちやすいもの。ゴミ出しや片付けの分担も明文化されているとストレスが減ります。'
    },
    {
        icon: <EmojiPeopleIcon color="warning" />,
        title: '9. ルームメイトとの相性やコミュニケーション',
        description: 'どんなに物件が良くても、住人同士の雰囲気や価値観が合わないとトラブルの元。入居前の顔合わせや事前確認は入念に。'
    },
    {
        icon: <HomeIcon color="secondary" />,
        title: '10. 立地とアクセスの良さ',
        description: '最寄り駅からの距離、周辺のスーパーやコンビニ、カフェなど。日常の利便性も住み心地に大きく影響します。'
    }
];

const improvementTips = [
    '自分専用の収納やカーテンでプライベート感を演出',
    '掃除・ゴミ出しのカレンダーをみんなで作成',
    '冷蔵庫内やキッチン用品はラベルで管理',
    '週に1回の「おしゃべりミーティング」で不満や要望を共有',
    'リビングのインテリアや照明を季節ごとにプチ模様替え',
    'ネット速度が落ちたら、ルーターの見直しや増設も検討',
    'シェア用の掲示板アプリやグループチャットを活用',
    '音に配慮したイヤホンや防音カーテンの導入',
    '玄関やバスルームに消臭・除菌グッズを常備',
    'イベントや誕生日会で良い関係性を築く'
];

const experienceStories = [
    {
        name: "Aさん（30代・会社員）",
        comment: "日当たりとリビングの広さで物件を選んだら、毎日が快適になりました。朝食を2人で囲んだり、自然光の中で読書したり…ルームシェアの醍醐味を感じています。",
    },
    {
        name: "Bさん（20代・学生）",
        comment: "当初は掃除当番の分担が曖昧でトラブルになりかけましたが、アプリで管理するようにしてから一気にスムーズに。小さな工夫で住み心地が大きく変わりました。",

    },
    {
        name: "Cさん（20代・クリエイター）",
        comment: "専用スペースにこだわって個別のデスクを設置。集中したい時とみんなで過ごす時間のバランスが取れるようになり、仕事もプライベートも充実しています。",

    }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold',
                                        borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <ListItemIcon sx={{ minWidth: 32, color: '#90caf9' }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left', pl: 0 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、多様なライフスタイルに合わせて人気が高まっている「ルームシェア」。賃料を抑え、さまざまな人と交流できる反面、実際に住み始めてみると「思っていたより快適じゃない…」と感じることも。<br />
                    本記事では、住み心地の良いルームシェア物件の条件や、快適な生活を送るためのコツ、実際の体験談をもとに、あなたのルームシェア生活をもっと素敵にするためのポイントを徹底解説します。
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>

                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            ルームシェアの最大の魅力は、経済的メリットや多様な出会いだけでなく、「心地よい暮らし」をいかに実現できるかにあります。<br />
                            これから紹介する10のチェックポイントと具体的な工夫を知れば、あなたも理想の住み心地を手に入れられるはず。まずは住み心地を左右する主な要素から見ていきましょう。
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* 住み心地ポイント */}
            <Box id="points" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    住み心地を左右する10のポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア生活の「住み心地」は、物件のスペックだけでなく、住人同士の関係や日々の運用ルールにも大きく左右されます。ここでは、実際に多くの入居者が重視している10のポイントを紹介します。
                </Typography>
                <Grid container spacing={3}>
                    {roomSharePoints.map((point, idx) => (
                        <Grid item xs={12} sm={6} key={point.title}>
                            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    {point.icon}
                                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        {point.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body2">{point.description}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* 工夫・アイデア */}
            <Box id="tips" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    生活の工夫と住み心地向上アイデア
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    住み心地をワンランクアップさせるには、ちょっとした工夫やアイデアが大切です。実際のシェアハウスで取り入れられている工夫をいくつかご紹介します。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f9fbe7' }}>
                    <List>
                        {improvementTips.map((tip, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                                <ListItemIcon>
                                    <CheckCircleIcon color="success" />
                                </ListItemIcon>
                                <ListItemText primary={tip} />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
                <Box
                    component="img"
                    src="/images/blog/organized-kitchen_roomshare-comfort-10points.jpg"
                    alt="清潔で整理整頓されたルームシェアのキッチン。ラベルで管理された調味料、専用スペース分けされた食器棚、明るい照明と木目調カウンターで快適な料理時間を演出。"
                    sx={{ width: '100%', borderRadius: 2, boxShadow: 1, mt: 2 }}
                />
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                    キッチンや収納の工夫は、共用スペースのストレス軽減に直結します。ラベリングや分担管理で快適さアップ。
                </Typography>
            </Box>

            {/* 体験談 */}
            <Box id="experiences" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    体験談から学ぶ住み心地アップのコツ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    実際にルームシェア生活を送る先輩たちのリアルな声には、住み心地を良くするヒントがたくさん詰まっています。ここでは3つの体験談をご紹介します。
                </Typography>
                <Grid container spacing={3}>
                    {experienceStories.map((story, idx) => (
                        <Grid item xs={12} md={4} key={story.name}>
                            <Card sx={{ maxWidth: 345, mx: 'auto', borderRadius: 2, height: '100%' }}>
                                <Box
                                    component="img"
                                    src={story.image}
                                    alt={story.alt}
                                    sx={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: '8px 8px 0 0' }}
                                />
                                <CardContent>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        {story.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {story.comment}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの「住み心地」は、物件の設備や立地だけでなく、日々の小さな工夫や住人同士の協力、そして自分なりの快適さを追求する姿勢によって大きく変わります。<br />
                    ご紹介した10のポイントとアイデア、先輩たちの体験談を参考に、あなたも理想のルームシェアライフを実現してください。
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label="ルームシェア" color="primary" variant="outlined" />
                    <Chip label="住み心地" color="secondary" variant="outlined" />
                    <Chip label="快適な暮らし" color="success" variant="outlined" />
                    <Chip label="シェアハウス" color="info" variant="outlined" />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-comfort-10points',
    slug: 'roomshare-comfort-10points',
    title: 'ルームシェアの「住み心地」を左右するポイント10選',
    excerpt: '住み心地の良い物件の条件や生活の工夫、住み心地向上アイデア、体験談までをまとめたルームシェア初心者必見の記事。',
    coverImage: '/images/blog/cover-shiny-livingroom_roomshare-comfort-10points.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。白を基調としたインテリアとグリーンの観葉植物がアクセントとなり、心地よさと一体感が感じられる空間。',
    publishedAt: '2024-06-10T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '住み心地', '生活アイデア'],
    tags: ['ルームシェア', '住み心地', '体験談', 'シェアハウス', '暮らしの工夫'],
    author: {
        name: '佐藤 蓮',
        avatar: '/images/avatars/roomshare-expert.jpg',
        bio: '住まいとライフスタイルを専門とし、数多くのルームシェア物件のマッチングや運営を手掛ける。シェア生活を快適にするための実践的なアドバイスが好評。',
        profilePicture: '/images/avatars/roomshare-expert.jpg'
    },
    viewCount: 1246,
    contentComponent: PostContent
};

export default post;